import React, { type FunctionComponent } from 'react'
import { useParams } from 'react-router'
import { css } from '@emotion/react'
import { Button, Text, spacing, useBooleanState } from '@retailer-platform/shared-components'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { useDashMessages } from 'intl/intl.hooks'
import { type DashMessages } from 'intl/intl.types'
import { type VisibilityConditionQueryParams } from 'domains/banners/types/visibilityCondition.types'
import { VisibilityConditionDeleteModal } from 'domains/banners/components/visibility-conditions-list/components/visibility-conditions/VisibilityConditionDeleteModal'

interface AttributeDetail {
  name: string
  label: string
  value: any
}

interface Props {
  visibilityConditionId: string
  title: DashMessages
  attributes: AttributeDetail[]
  conditionName: string
}

const sectionContainer = css({
  display: 'flex',
  flexFlow: 'row wrap',
  justifyContent: 'flex-start',
  '& > div': {
    flexBasis: '33%',
  },
})

const attributesWrapper = css({
  display: 'flex',
  flexFlow: 'row wrap',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
})

const filterContainer = css({
  display: 'flex',
  justifyContent: 'flex-end',
  margin: '0 auto',
  maxWidth: '960px',
  paddingRight: spacing.X4,
  paddingBottom: spacing.X20,
})

export const CommonDetail: FunctionComponent<Props> = ({
  visibilityConditionId,
  title,
  attributes,
  conditionName,
}) => {
  const { placementId } = useParams<VisibilityConditionQueryParams>()

  const [modalOpen, showModal, hideModal] = useBooleanState(false)

  const attributeLabels = {}
  attributes.forEach(({ name, label }) => {
    attributeLabels[name] = label
  })

  const messages = useDashMessages({
    emptyValue: 'bannersDomain.field.empty.value',
    deleteButton: 'bannersDomain.details.visibility-condition.deleteButton',
    ...attributeLabels,
  })

  return (
    <MerchandisingFormSection titleId={title}>
      <div css={sectionContainer}>
        {attributes.map(({ name, value }) => {
          const displayValue = Array.isArray(value) ? value.join(', ') : String(value)
          return (
            <div key={`${conditionName}-${name}`}>
              <Text weight="semibold">{messages[name]}</Text>
              <Text size="medium" css={attributesWrapper}>
                {displayValue || messages.emptyValue}
              </Text>
            </div>
          )
        })}
      </div>
      <div css={filterContainer}>
        <div>
          <Button onClick={showModal} variant="detrimental">
            {messages.deleteButton}
          </Button>
        </div>
      </div>
      <VisibilityConditionDeleteModal
        placementId={placementId}
        placementName={'Banner Placement'}
        isOpen={modalOpen}
        onCancel={hideModal}
        visibilityConditionTypeName={conditionName}
        visibilityConditionId={visibilityConditionId}
      />
    </MerchandisingFormSection>
  )
}
